import { ChartFrame } from '../components/charts/ChartFrame';
import { TrendChart } from '../components/charts/TrendChart';
import { BarSeries } from '../components/charts/BarSeries';
import { HeroTile, StatTile } from '../components/tiles/StatTile';
import { compact, duration, full, longDate, percent, shortDate } from '../lib/format';
import { platformSeries, scaleToFilter, visibleSegments } from '../lib/platform';
import { PLATFORM_LABELS, platformsFor, sumFor } from '../data/types';
import type { PlatformFilter, Snapshot } from '../data/types';

interface Props {
  snap: Snapshot;
  filter: PlatformFilter;
}

export function Overview({ snap, filter }: Props) {
  const series = platformSeries(filter);
  const active = platformsFor(filter);
  const dau = snap.activeUsers.dau;
  const latest = dau[dau.length - 1];
  const liveTotal = sumFor(snap.liveNow, filter);

  const heroValue = sumFor(latest, filter);
  const heroHistory = dau.map((d) => sumFor(d, filter));

  // Growth = installs minus churn, the number that actually answers "are we
  // growing". Two series against a shared zero baseline.
  const growth = snap.activeUsers.installs.map((p, i) => ({
    date: p.date,
    installs: sumFor(p, filter),
    churned: -sumFor(snap.activeUsers.churned[i], filter),
  }));

  const engines = visibleSegments(snap.searchEngines, filter);
  const topCountries = snap.geo.slice(0, 8);
  const geoMax = Math.max(...topCountries.map((g) => g.users));

  // Platform split of the latest day — the headline "who runs what".
  const splitTotal = active.reduce((sum, p) => sum + latest[p], 0) || 1;

  return (
    <>
      <div className="grid">
        <div data-span style={{ gridColumn: 'span 4' }}>
          <HeroTile
            label="Daily active users"
            value={full(heroValue)}
            delta={snap.summary.dau.delta}
            history={heroHistory}
            meta={
              <span className="chip">
                <span className="dot live-dot" style={{ background: 'var(--status-good)' }} />
                {full(liveTotal)} online now
              </span>
            }
          />
        </div>

        {/* Eight tiles that reflow 4→2→1 against the width of this column
            rather than the viewport's — at 1220px the four-across row was
            141px per tile, which is what pushed sparklines and deltas out
            through the card edge in the first place. */}
        <div data-span className="stat-grid" style={{ gridColumn: 'span 8' }}>
          <div className="grid stat-grid__inner">
            <StatTile
              label="Monthly active"
              value={compact(scaleToFilter(snap.summary.mau.value, filter, latest))}
              delta={snap.summary.mau.delta}
              history={snap.summary.mau.history}
            />
            <StatTile
              label="New installs"
              value={compact(scaleToFilter(snap.summary.newUsers.value, filter, latest))}
              delta={snap.summary.newUsers.delta}
              history={snap.summary.newUsers.history}
            />
            <StatTile
              label="Stickiness"
              value={percent(snap.summary.stickiness.value)}
              delta={snap.summary.stickiness.delta}
              history={snap.summary.stickiness.history}
              foot="DAU ÷ MAU"
            />
            <StatTile
              label="Avg session"
              value={duration(snap.summary.avgSessionSec.value)}
              delta={snap.summary.avgSessionSec.delta}
              history={snap.summary.avgSessionSec.history}
            />
            <StatTile
              label="Sessions / user / day"
              value={snap.summary.sessionsPerUser.value.toFixed(1)}
              delta={snap.summary.sessionsPerUser.delta}
              history={snap.summary.sessionsPerUser.history}
            />
            <StatTile
              label="Page loads"
              value={compact(scaleToFilter(snap.summary.pageLoads.value, filter, latest))}
              delta={snap.summary.pageLoads.delta}
              history={snap.summary.pageLoads.history}
              foot="per day"
            />
            <StatTile
              label="Crash-free sessions"
              value={percent(snap.summary.crashFreeRate.value, 2)}
              delta={snap.summary.crashFreeRate.delta}
              history={snap.summary.crashFreeRate.history}
              sparkColor="var(--status-good)"
            />
            <StatTile
              label="Churned users"
              value={compact(sumFor(snap.activeUsers.churned[snap.activeUsers.churned.length - 1], filter))}
              invertDelta
              delta={-snap.summary.newUsers.delta * 0.3}
              foot="last day of range"
            />
          </div>
        </div>

        <div data-span style={{ gridColumn: 'span 8' }}>
          <ChartFrame
            title="Daily active users by platform"
            subtitle={`${dau.length}-day trend`}
            table={{
              columns: ['Date', ...active.map((p) => PLATFORM_LABELS[p]), 'Total'],
              rows: dau.map((d) => [
                longDate(d.date),
                ...active.map((p) => full(d[p])),
                full(sumFor(d, filter)),
              ]),
            }}
          >
            <TrendChart data={dau} series={series} variant={series.length === 1 ? 'area' : 'line'} height={272} />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 4' }}>
          <ChartFrame
            title="Platform split"
            subtitle="Share of daily actives today"
            table={{
              columns: ['Platform', 'Users', 'Share'],
              rows: active.map((p) => [
                PLATFORM_LABELS[p],
                full(latest[p]),
                percent(latest[p] / splitTotal),
              ]),
            }}
          >
            <div className="rank">
              {active.map((p) => (
                <div className="rank__row" key={p}>
                  <span className="rank__name">
                    <span className="dot" style={{ background: series.find((s) => s.key === p)?.color }} />
                    <span>{PLATFORM_LABELS[p]}</span>
                  </span>
                  <span className="rank__val">{percent(latest[p] / splitTotal)}</span>
                  <div className="rank__bar bar-track">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${(latest[p] / splitTotal) * 100}%`,
                        background: series.find((s) => s.key === p)?.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 7' }}>
          <ChartFrame
            title="Growth"
            subtitle="New installs against churn, same baseline"
            table={{
              columns: ['Date', 'Installs', 'Churned', 'Net'],
              rows: growth.map((g) => [
                longDate(g.date),
                full(g.installs),
                full(-g.churned),
                full(g.installs + g.churned),
              ]),
            }}
          >
            <BarSeries
              data={growth}
              categoryKey="date"
              series={[
                { key: 'installs', label: 'New installs', color: 'var(--series-3)' },
                { key: 'churned', label: 'Churned', color: 'var(--series-8)' },
              ]}
              stacked
              diverging
              height={252}
              valueFormat={(n) => compact(Math.abs(n))}
              categoryFormat={shortDate}
            />
            <p className="card__sub" style={{ marginTop: 8 }}>
              Churn is drawn below the baseline. Net growth is the visible gap.
            </p>
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 5' }}>
          <ChartFrame
            title="Where users are"
            // Geo arrives without a platform split, so say so rather than let
            // the header filter imply a breakdown that isn't in the data.
            subtitle={
              filter === 'all' ? 'Top countries by DAU' : 'Top countries by DAU · fleet-wide, not split by platform'
            }
            table={{
              columns: ['Country', 'Users', 'Share'],
              rows: snap.geo.map((g) => [g.country, full(g.users), percent(g.share)]),
            }}
          >
            <div className="rank">
              {topCountries.map((g) => (
                <div className="rank__row" key={g.code}>
                  <span className="rank__name">
                    <span className="rank__code">{g.code}</span>
                    <span>{g.country}</span>
                  </span>
                  <span className="rank__val">{percent(g.share)}</span>
                  <div className="rank__bar bar-track">
                    <div
                      className="bar-fill"
                      style={{ width: `${(g.users / geoMax) * 100}%`, background: 'var(--seq-5)' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 12' }}>
          <ChartFrame
            title="Default search engine"
            subtitle="Share of active users"
            table={{
              columns: ['Engine', ...active.map((p) => PLATFORM_LABELS[p]), 'Total'],
              rows: engines.map((e) => [
                e.name,
                ...active.map((p) => full(e[p])),
                full(sumFor(e, filter)),
              ]),
            }}
          >
            <BarSeries
              data={engines}
              categoryKey="name"
              series={series}
              layout="horizontal"
              stacked={series.length > 1}
              height={260}
              categoryWidth={96}
            />
          </ChartFrame>
        </div>
      </div>

      <p className="card__sub">
        Range ends {shortDate(latest.date)} · generated {new Date(snap.generatedAt).toLocaleString()}
      </p>
    </>
  );
}
